<template>
  <div class="column full-height">
    <header
      class="row items-center justify-between q-px-2x-md full-width no-wrap"
      :style="{ height: '76px' }"
    >
      <span
        class="text-lg text-neutral text-weight-medium"
        :style="{ maxWidth: '75%' }"
      >
        {{ heading }}
      </span>

      <PBtn color="primary" flat round @click="onClose">
        <CloseIcon class="icon" />
      </PBtn>
    </header>
    <q-separator color="neutral-40 q-mx-xl" />
    <div ref="scrollTarget" class="q-px-2x-md q-py-md col overflow-auto">
      <q-linear-progress v-if="loading" indeterminate />
      <q-infinite-scroll
        v-else
        :key="requestVersion"
        :scroll-target="scrollTarget"
        :disable="!hasMore || !!loadError"
        :offset="150"
        @load="loadMore"
      >
        <PTimeline>
          <div v-if="!timelines.length && !loadError">No activity yet!</div>
          <PTimelineEntry
            v-for="(timeline, index) in timelines"
            :key="timeline._id || index"
          >
            <template #default>
              <div class="row items-start no-wrap">
                <q-avatar
                  v-if="showActor"
                  class="q-mr-sm"
                  color="info-20"
                  font-size="15px"
                  size="25px"
                  text-color="info-70"
                >
                  <span class="text-uppercase">
                    {{ getAvatarInitials(timeline?.actor?.user_name) }}
                  </span>
                </q-avatar>
                <div class="column">
                  <div class="text-weight-regular text-sm">
                    <div
                      v-for="(line, lineIndex) in formatDescription(timeline)"
                      :key="lineIndex"
                    >
                      <span
                        v-for="(part, partIndex) in line"
                        :key="partIndex"
                        :class="{ 'text-weight-semibold': part.bold }"
                        >{{ part.text }}</span
                      >
                    </div>
                  </div>
                  <div
                    v-if="hasDetails(timeline)"
                    class="text-weight-medium text-xs"
                  >
                    {{ formatDetails(timeline) }}
                  </div>
                  <div class="text-weight-regular text-xs text-neutral-70">
                    {{ formatDate(timeline.event_time, "DD MMM YYYY, h:mm a") }}
                  </div>
                </div>
              </div>
            </template>
          </PTimelineEntry>
        </PTimeline>
        <template #loading>
          <div class="row justify-center q-my-md">
            <q-spinner-dots color="primary" size="40px" />
          </div>
        </template>
      </q-infinite-scroll>
      <div v-if="loadError" class="q-my-md" role="alert">
        <p>{{ loadError }}</p>
        <PBtn label="Retry" flat color="primary" @click="retry" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { date } from "quasar";
import { index as getAuditLogs } from "src/api/audit-logs";
import CloseIcon from "assets/icons/close.svg";
import { getAvatarInitials } from "src/assets/scripts/utilities/helpers";
import { useChangeCase } from "@vueuse/integrations/useChangeCase.mjs";

const { formatDate } = date;

const timelines = ref([]);

const { timelinesDrawerState } = storeToRefs(useAuditLogsStore());
const { toggleTimelinesDrawer } = useAuditLogsStore();

const props = defineProps({
  entityId: {
    type: String,
    default: () => "",
  },
  entityName: {
    type: String,
    default: () => "",
  },
  heading: {
    type: String,
    default: () => "Timelines",
  },
  action: {
    type: String,
    default: () => "",
  },
  showActor: {
    type: Boolean,
    default: false,
  },
  detailFields: {
    type: Array,
    default: () => [],
  },
});

const loading = ref(false);
const scrollTarget = ref(null);
const loadError = ref("");
const hasMore = ref(false);
const requestVersion = ref(0);
const pageSize = 25;
let offset = 0;
let fetching = false;

// Render only bold markers and line breaks; API text remains escaped by Vue.
const formatDescription = (timeline) => {
  const markdown = timeline.md_description?.trim();
  const description = markdown || timeline.description || "-";

  return description
    .split(/\r\n|\n|\r/)
    .map((line) => line.trim())
    .filter((line) => line && line !== ",")
    .map((line) =>
      (markdown ? line.split(/(\*\*.*?\*\*)/g) : [line])
        .filter(Boolean)
        .map((text) => {
          const bold =
            !!markdown && text.startsWith("**") && text.endsWith("**");

          return { text: bold ? text.slice(2, -2) : text, bold };
        }),
    );
};

// A detail field can be a plain key string (rendered as-is) or an object
// `{ key, case }` where `case` is any useChangeCase type (e.g. "capitalCase").
const normalizeField = (field) =>
  typeof field === "string" ? { key: field } : field;

const hasDetails = (timeline) =>
  props.detailFields.some(
    (field) => timeline?.current_state?.[normalizeField(field).key],
  );

const formatDetails = (timeline) =>
  props.detailFields
    .map((field) => {
      const { key, case: caseType } = normalizeField(field);

      const value = timeline?.current_state?.[key] || "";

      return caseType ? useChangeCase(value, caseType).value : value;
    })
    .join(" | ");

const onClose = () => {
  toggleTimelinesDrawer({
    open: false,
  });
};

// Advance the offset only after success so Retry requests the failed page again.
const fetchPage = async () => {
  const version = requestVersion.value;

  fetching = true;

  loadError.value = "";

  try {
    const response = await getAuditLogs({
      params: {
        entity_id: props.entityId,
        entity: props.entityName,
        action: props.action,
        offset,
        page_size: pageSize,
      },
    });

    // A previous entity's request can finish after the drawer has reopened.
    if (version !== requestVersion.value) return;

    const data = response?.data?.data;
    const page = Array.isArray(data?.data) ? data.data : [];

    timelines.value = [...timelines.value, ...page];

    offset += page.length;

    hasMore.value = page.length > 0 && offset < (data?.count ?? offset);
  } catch {
    if (version === requestVersion.value) {
      loadError.value = "Unable to load activity logs. Please try again.";
    }
  } finally {
    if (version === requestVersion.value) {
      fetching = false;

      loading.value = false;
    }
  }
};

const loadMore = async (_index, done) => {
  if (fetching || !hasMore.value) {
    done(!hasMore.value);

    return;
  }

  await fetchPage();

  done(!hasMore.value || !!loadError.value);
};

const retry = async () => {
  if (fetching) return;

  const version = requestVersion.value;

  loading.value = timelines.value.length === 0;

  await fetchPage();

  // Remount the scroll observer after a failed page stopped it.
  if (version === requestVersion.value) requestVersion.value += 1;
};

watch(
  () => [
    unref(timelinesDrawerState).open,
    props.entityId,
    props.entityName,
    props.action,
  ],
  ([open]) => {
    requestVersion.value += 1;

    timelines.value = [];

    offset = 0;

    fetching = false;

    loadError.value = "";

    hasMore.value = !!open;

    loading.value = !!open;

    if (open) void fetchPage();
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  requestVersion.value += 1;
});
</script>
